import { db, normalizeFolderId } from './db';
import { encryptChunk } from './cryptoClient';
import { createFileKey, openFileKey } from './crypto';
import { createDeleteProof } from './deleteProof';
import { sealRecord, openRecords } from './localIndex';

// Dateien laufender Uploads, nur im Arbeitsspeicher (siehe runUploadJob).
const resumeFiles = new Map();

/** Alte Klartextkopien aus der Zeit vor dem 22.10.26 entfernen. Einmal beim Start. */
export async function dropLegacyPlaintextResumeCopies() {
  try { await db.pendingFileBlobs.clear(); } catch (_) { /* nichts zu tun */ }
}
import { createClientId } from './journal';
import { pushMachineEvent } from './machineLog';

const API_URL = '/api/tresor';
const MAX_CHUNK_RETRIES = 5;
export const CHUNK_SIZE = 5 * 1024 * 1024;

// Cheap, real per-chunk fingerprint for the live UI: 5 hex groups (2 bytes each) taken from
// the encrypted chunk's trailing AES-GCM auth tag. No hashing — just reads bytes already there.
export function chunkFingerprint(encryptedChunk) {
  const bytes = encryptedChunk instanceof Uint8Array ? encryptedChunk : new Uint8Array(encryptedChunk);
  const off = Math.max(0, bytes.length - 16);
  const hex = (b) => (b ?? 0).toString(16).padStart(2, '0');
  return [0, 2, 4, 6, 8].map((i) => hex(bytes[off + i]) + hex(bytes[off + i + 1]));
}

export function createFileUuid() {
  if (window.crypto.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

const DELETE_UPLOAD_TIMEOUT_MS = 45000;

async function fetchDeleteWithTimeout(authenticatedFetch, uploadId, masterKey) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), DELETE_UPLOAD_TIMEOUT_MS);
  try {
    const deleteProof = await createDeleteProof(authenticatedFetch, masterKey, 'trash', [uploadId]);
    return await authenticatedFetch(`${API_URL}/upload/${uploadId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deleteProof }),
      signal: controller.signal
    });
  } finally {
    clearTimeout(timer);
  }
}

async function parseDeleteUploadError(res) {
  const raw = await res.text().catch(() => '');
  let msg = 'Cloud-Löschen fehlgeschlagen';
  try {
    const err = JSON.parse(raw);
    msg = err.message || err.error || msg;
  } catch {
    if (res.status === 404 && raw.includes('Cannot DELETE')) {
      msg = 'Server-Update ausstehend. Bitte kurz warten und erneut versuchen.';
    } else if (raw) {
      msg = `Cloud-Löschen fehlgeschlagen (HTTP ${res.status})`;
    }
  }
  return msg;
}

/** Remove completed cloud upload (S3 chunks + server quota). Idempotent on 404. */
export async function deleteCloudUpload(authenticatedFetch, uploadId, { retries = 2, masterKey = null } = {}) {
  if (!uploadId) return { ok: true, skipped: true };

  let lastError = null;
  for (let attempt = 0; attempt < retries; attempt += 1) {
    if (attempt > 0) {
      await new Promise((r) => setTimeout(r, 400 * attempt));
    }
    try {
      const res = await fetchDeleteWithTimeout(authenticatedFetch, uploadId, masterKey);
      if (res.status === 404) return { ok: true, alreadyGone: true };
      if (res.ok) return res.json().catch(() => ({ ok: true }));
      lastError = new Error(await parseDeleteUploadError(res));
    } catch (err) {
      if (err?.name === 'AbortError') {
        lastError = new Error('Cloud-Löschen hat zu lange gedauert. Bitte erneut versuchen.');
      } else {
        lastError = err;
      }
    }
  }
  throw lastError || new Error('Cloud-Löschen fehlgeschlagen');
}

/** Abort in-flight pending uploads whose target folder is being deleted. */
export async function abortPendingUploadsForFolders(authenticatedFetch, folderIds) {
  const idSet = new Set(folderIds.map((id) => normalizeFolderId(id)));
  const pending = await db.pendingUploads.toArray();
  for (const record of pending) {
    if (idSet.has(normalizeFolderId(record.folderId))) {
      await abortPendingUpload(authenticatedFetch, record.id, record.uploadId);
    }
  }
}

export async function abortPendingUpload(authenticatedFetch, pendingId, uploadId) {
  if (uploadId) {
    await authenticatedFetch(`${API_URL}/upload/abort`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uploadId })
    }).catch(() => {});
  }
  await db.pendingUploads.delete(pendingId).catch(() => {});
  await db.pendingFileBlobs.delete(pendingId).catch(() => {});
  resumeFiles.delete(pendingId);
}

const CHUNK_PUT_TIMEOUT_MS = 60000;

// Vorrat an Upload-Adressen: bis zu 8 Bloecke mit einer Anfrage (sign-batch) statt je Block eine.
// Spart bei langer Antwortzeit (Mobilfunk) spuerbar Zeit. Jede Adresse wird genau einmal benutzt;
// Wiederholungen nach Fehlern holen weiter eine frische Einzeladresse.
const SIGN_BATCH = 8;
function createUploadUrlPool(authenticatedFetch, uploadId, totalChunks, isDone) {
  const cache = new Map(); // idx -> { url, until }
  let inflight = null;
  const fill = async (fromIdx) => {
    const want = [];
    for (let i = fromIdx; i < totalChunks && want.length < SIGN_BATCH; i++) {
      if (!isDone(i) && !cache.has(i)) want.push(i);
    }
    if (!want.length) return;
    try {
      const res = await authenticatedFetch(`${API_URL}/upload/sign-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uploadId, chunkIndexes: want })
      });
      if (!res.ok) return; // dann eben einzeln
      const { urls = {}, expiresIn = 120 } = await res.json();
      const until = Date.now() + Math.max(10, expiresIn - 30) * 1000;
      for (const [i, url] of Object.entries(urls)) cache.set(Number(i), { url, until });
    } catch (_) { /* einzeln weiter */ }
  };
  return {
    async take(idx) {
      for (let attempt = 0; attempt < 2; attempt++) {
        const e = cache.get(idx);
        if (e && e.until > Date.now()) { cache.delete(idx); return e.url; }
        if (!inflight) inflight = fill(idx).finally(() => { inflight = null; });
        await inflight;
      }
      const e = cache.get(idx);
      if (e && e.until > Date.now()) { cache.delete(idx); return e.url; }
      return null;
    }
  };
}

// Gleichzeitigkeit passt sich der Leitung an: Start bei 3 parallelen Bloecken, hoch bis 6, solange
// jede Stufe den gemessenen Durchsatz noch um mindestens 10 % steigert. Bringt eine Stufe nichts oder
// muss ein Block wiederholt werden, geht es eine Stufe zurueck und bleibt dort. Der gelernte Wert gilt
// fuer die naechsten Dateien dieser Sitzung.
const PAR_MIN = 2;
const PAR_START = 3;
const PAR_MAX = 6;
let learnedParallel = PAR_START;

function createParallelControl() {
  let target = learnedParallel;
  let ceiling = PAR_MAX;
  let prevRate = null;
  let winStart = performance.now();
  let winBytes = 0;
  let winChunks = 0;
  const resetWindow = () => { winStart = performance.now(); winBytes = 0; winChunks = 0; };
  const stepDown = () => {
    target = Math.max(PAR_MIN, target - 1);
    ceiling = target;
    learnedParallel = target;
    resetWindow();
  };
  return {
    get target() { return target; },
    onSuccess(bytes, hadRetry) {
      if (hadRetry) { stepDown(); prevRate = null; return; }
      winBytes += bytes;
      winChunks += 1;
      if (winChunks < target * 2) return; // jede Stufe ueber mindestens zwei Runden messen
      const rate = winBytes / Math.max(1, performance.now() - winStart);
      if (prevRate !== null && rate < prevRate * 1.1) {
        stepDown(); // die letzte Erhoehung brachte nichts
        return;
      }
      prevRate = rate;
      if (target < ceiling) target += 1;
      learnedParallel = target;
      resetWindow();
    }
  };
}

async function uploadChunkWithRetry(authenticatedFetch, uploadId, chunkIndex, encryptedChunk, getCancelled, cancelSignal, logMeta = {}, urlPool = null) {
  let retries = 0;
  while (retries < MAX_CHUNK_RETRIES) {
    if (getCancelled && getCancelled()) return { ok: false, cancelled: true };
    if (cancelSignal && cancelSignal.aborted) return { ok: false, cancelled: true };
    try {
      // 1) Presigned PUT URL: beim ersten Versuch aus dem Vorrat (gebuendelt geholt), bei
      //    Wiederholungen frisch und einzeln, damit eine abgelaufene Adresse nie haengen bleibt.
      let url = retries === 0 && urlPool ? await urlPool.take(chunkIndex) : null;
      if (!url) {
        const signRes = await authenticatedFetch(`${API_URL}/upload/sign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uploadId, chunkIndex })
        });
        if (signRes.status === 501) return { ok: false, storageUnavailable: true };
        if (signRes.status === 404) return { ok: false, sessionExpired: true };
        if (!signRes.ok) throw new Error('sign_failed');
        ({ url } = await signRes.json());
      }
      if (!url) throw new Error('sign_no_url');
      // Für den Maschinenraum: nur Host und Objektpfad, niemals die signierte URL selbst.
      // Der Objektpfad ist hier der eigentliche Beleg. Er lautet userId/uploadId/index
      // und enthält keinen Dateinamen.
      let targetHost = '';
      let objectPath = '';
      try {
        const parsed = new URL(url);
        targetHost = parsed.host;
        objectPath = parsed.pathname.replace(/^\//, '');
      } catch { /* eine unparsebare URL scheitert gleich beim PUT */ }
      pushMachineEvent({
        kind: 'sign',
        fileName: logMeta.fileName,
        chunkIndex,
        totalChunks: logMeta.totalChunks,
        sameOrigin: true,
        authenticated: true,
        bodyBytes: 0,
        targetHost,
        objectPath
      });

      // 2) Upload the encrypted chunk DIRECTLY to object storage with a BARE fetch.
      //    No Authorization header: the presigned URL is the credential, and attaching
      //    the JWT would leak it to storage and break the SignedHeaders=host signature.
      //    A hard timeout is essential — a stalled PUT would otherwise hang the whole
      //    upload queue forever (and block unrelated actions like moving files to the bin).
      const chunkBody = encryptedChunk instanceof Blob
        ? encryptedChunk
        : new Blob([encryptedChunk]);
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), CHUNK_PUT_TIMEOUT_MS);
      // A shared cancel signal (from the user hitting Abbrechen) aborts the in-flight PUT
      // immediately, so no chunk lands after the session was aborted+swept → no orphans.
      const onCancel = () => controller.abort();
      if (cancelSignal) {
        if (cancelSignal.aborted) controller.abort();
        else cancelSignal.addEventListener('abort', onCancel, { once: true });
      }
      const putStart = performance.now();
      try {
        const putRes = await fetch(url, { method: 'PUT', body: chunkBody, signal: controller.signal });
        if (putRes.ok) {
          pushMachineEvent({
            kind: 'put',
            fileName: logMeta.fileName,
            chunkIndex,
            totalChunks: logMeta.totalChunks,
            sameOrigin: false,
            authenticated: false,
            cipherBytes: chunkBody.size,
            targetHost,
            objectPath,
            ms: performance.now() - putStart
          });
          return { ok: true, retries };
        }
        throw new Error('chunk_upload_failed');
      } finally {
        clearTimeout(timer);
        if (cancelSignal) cancelSignal.removeEventListener('abort', onCancel);
      }
    } catch (_) {
      if (getCancelled && getCancelled()) return { ok: false, cancelled: true };
      if (cancelSignal && cancelSignal.aborted) return { ok: false, cancelled: true };
      retries += 1;
      if (retries >= MAX_CHUNK_RETRIES) break;
      await new Promise((r) => setTimeout(r, 1000 * retries));
    }
  }
  return { ok: false, networkError: true };
}

async function buildLocalEncryptedBlob(file, activeKey, fileUuid, chunkSize, onChunkProgress, getActiveKey) {
  const totalChunks = Math.ceil(file.size / chunkSize);
  const encryptedChunks = [];
  for (let c = 0; c < totalChunks; c++) {
    if (getActiveKey && !getActiveKey()) throw new Error('UPLOAD_LOCKED');
    const start = c * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    onChunkProgress?.(c, totalChunks, end);
    const chunkBuffer = await file.slice(start, end).arrayBuffer();
    encryptedChunks.push(await encryptChunk(chunkBuffer, activeKey, fileUuid, c));
  }
  const totalLength = encryptedChunks.reduce((acc, chunk) => acc + chunk.length, 0);
  const combined = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of encryptedChunks) {
    combined.set(chunk, offset);
    offset += chunk.length;
  }
  return new Blob([combined]);
}

async function resolveFolderClientId(folderId) {
  const key = normalizeFolderId(folderId);
  if (key === 'root') return 'root';
  const folder = await db.folders.get(Number(key));
  return folder?.clientId || 'root';
}

async function finalizeLocalFile({
  file,
  activeKey,
  fileUuid,
  chunkSize,
  folderId,
  getFileType,
  formatBytes,
  onFinalizeProgress,
  getActiveKey,
  uploadId = null,
  totalChunks = null,
  skipLocalBlob = false,
  chunkKey = activeKey,
  wrappedFileKey = null
}) {
  const folderClientId = await resolveFolderClientId(folderId);
  const clientId = createClientId();
  let encryptedBlob = null;

  if (!skipLocalBlob) {
    encryptedBlob = await buildLocalEncryptedBlob(file, chunkKey, fileUuid, chunkSize, onFinalizeProgress, getActiveKey);
  }

  const fileId = await db.files.add(await sealRecord('file', {
    clientId,
    folderClientId,
    folderId: normalizeFolderId(folderId),
    name: file.name,
    type: getFileType(file.name),
    size: formatBytes(file.size),
    sizeBytes: file.size,
    originalType: file.type,
    encryptionId: fileUuid,
    wrappedFileKey,
    uploadId,
    totalChunks,
    chunkSize,
    createdAt: Date.now()
  }));

  if (encryptedBlob) {
    await db.fileBlobs.add({ id: fileId, blob: encryptedBlob });
  }

  return {
    fileId,
    clientId,
    folderClientId,
    uploadId,
    totalChunks,
    chunkSize,
    encryptionId: fileUuid,
    wrappedFileKey,
    name: file.name,
    sizeBytes: file.size,
    sizeLabel: formatBytes(file.size),
    mimeType: file.type || 'application/octet-stream',
    fileType: getFileType(file.name)
  };
}

/**
 * Local-only path when S3/cloud is not configured — full encrypt + IndexedDB, no server upload.
 * Lets you test UI, folders, download, and chunk progress without object storage.
 */
export async function runLocalOnlyUploadJob({
  file,
  activeKey,
  folderId,
  onProgress,
  getFileType,
  formatBytes,
  getActiveKey,
  onFileRecorded = null
}) {
  const fileUuid = createFileUuid();
  const chunkSize = CHUNK_SIZE;
  const totalChunks = Math.ceil(file.size / chunkSize);
  const totalBytes = file.size;
  const { fileKey, wrappedFileKey } = await createFileKey(activeKey, fileUuid);
  const progressId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const emit = (patch, remove = false) => {
    onProgress(progressId, { fileName: file.name, totalBytes, totalChunks, remove, ...patch });
  };

  const uiBreathMs = totalChunks === 1 ? 450 : 0;

  try {
    emit({ percent: 0, phase: 'waiting', chunkIndex: 0, bytesDone: 0 });
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    if (uiBreathMs) await new Promise((r) => setTimeout(r, uiBreathMs));
    const encryptedChunks = [];
    // Same rule as the cloud path: real value, read from the buffer we actually keep.
    let lastChunkHash = null;
    let lastChunkHashIndex = null;

    for (let c = 0; c < totalChunks; c++) {
      if (getActiveKey && !getActiveKey()) {
        emit({ percent: 0, phase: 'waiting', chunkIndex: 0, bytesDone: 0 }, true);
        return { ok: false, reason: 'locked_strict', message: 'Upload gestoppt: Tresor wurde streng gesperrt.' };
      }
      const sliceStart = c * chunkSize;
      const sliceEnd = Math.min(sliceStart + chunkSize, file.size);

      emit({
        percent: Math.round((c / totalChunks) * 80),
        phase: 'encrypting',
        chunkIndex: c + 1,
        bytesDone: sliceStart
      });
      if (uiBreathMs) await new Promise((r) => setTimeout(r, uiBreathMs));

      const chunkBuffer = await file.slice(sliceStart, sliceEnd).arrayBuffer();
      const encStart = performance.now();
      const encryptedChunk = await encryptChunk(chunkBuffer, fileKey, fileUuid, c);
      const encMs = performance.now() - encStart;
      encryptedChunks.push(encryptedChunk);
      lastChunkHash = chunkFingerprint(encryptedChunk);
      lastChunkHashIndex = c;
      pushMachineEvent({
        kind: 'encrypt',
        target: 'local',
        fileName: file.name,
        chunkIndex: c,
        totalChunks,
        plainBytes: chunkBuffer.byteLength,
        cipherBytes: encryptedChunk.length,
        fingerprint: lastChunkHash,
        ms: encMs
      });

      emit({
        percent: Math.round(((c + 0.6) / totalChunks) * 88),
        phase: 'storing',
        chunkIndex: c + 1,
        bytesDone: sliceEnd,
        chunkHash: lastChunkHash,
        chunkHashIndex: lastChunkHashIndex
      });
      if (uiBreathMs) await new Promise((r) => setTimeout(r, Math.round(uiBreathMs * 0.6)));
    }

    emit({
      percent: 90,
      phase: 'finalizing',
      chunkIndex: totalChunks,
      bytesDone: file.size,
      ...(lastChunkHash ? { chunkHash: lastChunkHash, chunkHashIndex: lastChunkHashIndex } : {})
    });

    const totalLength = encryptedChunks.reduce((acc, ch) => acc + ch.length, 0);
    const combined = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of encryptedChunks) {
      combined.set(chunk, offset);
      offset += chunk.length;
    }

    const folderClientId = await resolveFolderClientId(folderId);
    const clientId = createClientId();
    const fileId = await db.files.add(await sealRecord('file', {
      clientId,
      folderClientId,
      folderId: normalizeFolderId(folderId),
      name: file.name,
      type: getFileType(file.name),
      size: formatBytes(file.size),
      sizeBytes: file.size,
      originalType: file.type,
      encryptionId: fileUuid,
      wrappedFileKey,
      createdAt: Date.now()
    }));
    await db.fileBlobs.add({ id: fileId, blob: new Blob([combined]) });

    if (onFileRecorded) {
      const folderClientId = await resolveFolderClientId(folderId);
      await onFileRecorded({
        type: 'file_add',
        clientId,
        parentClientId: folderClientId,
        name: file.name,
        encryptionId: fileUuid,
        wrappedFileKey,
        uploadId: null,
        totalChunks: null,
        chunkSize: null,
        sizeBytes: file.size,
        sizeLabel: formatBytes(file.size),
        mimeType: file.type || 'application/octet-stream',
        fileType: getFileType(file.name)
      });
    }

    emit({ percent: 100, phase: 'done', chunkIndex: totalChunks, bytesDone: file.size });
    await new Promise((r) => setTimeout(r, 1200));
    emit({ percent: 100, phase: 'done', chunkIndex: totalChunks, bytesDone: file.size }, true);
    return { ok: true, fileName: file.name, localOnly: true };
  } catch (err) {
    console.error('Local-only upload failed', err);
    emit({ percent: 0, phase: 'waiting', chunkIndex: 0, bytesDone: 0 }, true);
    return { ok: false, reason: 'local_failed', message: `„${file.name}" konnte nicht lokal gespeichert werden.` };
  }
}

/**
 * Upload a File (fresh) or resume an existing pendingUploads row.
 */
export async function runUploadJob({
  file,
  pendingRecord,
  authenticatedFetch,
  activeKey,
  folderId,
  onProgress,
  getFileType,
  formatBytes,
  localOnly = false,
  getActiveKey,
  onFileRecorded = null,
  isCancelled = null,
  onCancelReady = null
}) {
  if (localOnly && !pendingRecord) {
    return runLocalOnlyUploadJob({
      file, activeKey, folderId, onProgress, getFileType, formatBytes, getActiveKey, onFileRecorded
    });
  }
  if (localOnly && pendingRecord) {
    return {
      ok: false,
      reason: 'cloud_pending',
      message: 'Ausstehende Uploads konnten nicht fortgesetzt werden.'
    };
  }
  let pendingId = pendingRecord?.id ?? null;
  let uploadId = pendingRecord?.uploadId ?? null;
  let fileUuid = pendingRecord?.fileUuid ?? createFileUuid();
  let chunkSize = pendingRecord?.chunkSize ?? null;
  let totalChunks = pendingRecord?.totalChunks ?? null;
  let startChunk = pendingRecord?.uploadedChunks ?? 0;
  // Schluessel je Datei. Eine wiederaufgenommene Datei muss mit DEMSELBEN Schluessel weiter
  // verschluesselt werden, deshalb liegt die Huelle im pendingUploads-Eintrag. Eintraege von
  // vor dem 22.10.26 haben keine und bleiben beim Hauptschluessel.
  let wrappedFileKey = pendingRecord?.wrappedFileKey ?? null;
  let chunkKey = activeKey;
  const targetFolderId = pendingRecord?.folderId ?? folderId;
  const totalBytes = pendingRecord?.totalSize ?? file?.size ?? 0;
  // One abort controller for the whole job; the cancel handler triggers it to stop all
  // in-flight chunk PUTs at once. Registered with the caller once pendingId is known.
  const cancelController = new AbortController();

  const emit = (patch, remove = false) => {
    onProgress(pendingId, {
      fileName: file?.name ?? pendingRecord?.fileName ?? 'Datei',
      totalBytes,
      totalChunks: totalChunks ?? 0,
      remove,
      ...patch
    });
  };

  try {
    if (!pendingRecord) {
      const initRes = await authenticatedFetch(`${API_URL}/upload/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileSize: file.size })
      });
      if (!initRes.ok) {
        const err = await initRes.json().catch(() => ({}));
        return {
          ok: false,
          reason: 'init_failed',
          // Fehlercode durchreichen: die Oberflaeche unterscheidet daran volle Quota in der
          // Testphase (dann Buchungsdialog) von Schreibschutz und echten Fehlern.
          code: err.error || null,
          trial: !!err.trial,
          message: err.message || err.error || 'Upload konnte nicht gestartet werden.'
        };
      }
      const initData = await initRes.json();
      uploadId = initData.uploadId;
      chunkSize = initData.chunkSize;
      totalChunks = Math.ceil(file.size / chunkSize);
      startChunk = 0;

      pendingId = uploadId;
      if (onCancelReady) onCancelReady(pendingId, cancelController);
      ({ fileKey: chunkKey, wrappedFileKey } = await createFileKey(activeKey, fileUuid));
      await db.pendingUploads.add(await sealRecord('pending', {
        id: uploadId,
        fileName: file.name,
        totalSize: file.size,
        totalChunks,
        uploadedChunks: 0,
        completedChunks: [],
        uploadId,
        fileUuid,
        wrappedFileKey,
        chunkSize,
        folderId: targetFolderId,
        originalType: file.type || 'application/octet-stream'
      }));
      // Bis 22.10.26 lag hier eine Kopie der Datei in IndexedDB, damit ein Upload auch nach
      // dem Neuladen weiterlaufen konnte. Diese Kopie war der KLARTEXT der Datei und blieb
      // ueber das Sperren hinaus auf der Platte. Jetzt nur noch im Arbeitsspeicher dieses Tabs:
      // Fortsetzen klappt nach Netzaussetzern und nach dem Sperren, nicht nach dem Neuladen.
      resumeFiles.set(pendingId, file);
      emit({ percent: 0, phase: 'waiting', chunkIndex: 0, bytesDone: 0 });
    } else {
      if (!uploadId || !chunkSize || !totalChunks) {
        await abortPendingUpload(authenticatedFetch, pendingId, uploadId);
        return { ok: false, reason: 'corrupt_pending', message: 'Unvollständiger Upload-Eintrag bereinigt.' };
      }
      const blobRow = resumeFiles.has(pendingId) ? { blob: resumeFiles.get(pendingId) } : null;
      if (!blobRow?.blob) {
        await abortPendingUpload(authenticatedFetch, pendingId, uploadId);
        return { ok: false, reason: 'missing_blob', message: `„${pendingRecord.fileName}" konnte nicht fortgesetzt werden (Datei-Cache fehlt).` };
      }
      file = new File([blobRow.blob], pendingRecord.fileName, {
        type: pendingRecord.originalType || 'application/octet-stream'
      });
      if (wrappedFileKey) chunkKey = await openFileKey(activeKey, fileUuid, wrappedFileKey);
      if (onCancelReady) onCancelReady(pendingId, cancelController);
    }

    // ---- Bounded-concurrency upload pool ----------------------------------------------------
    // K workers pull chunk indices from a shared dispenser and each do encrypt → sign → PUT.
    // Encryption serializes through the single crypto worker while up to K PUTs run in parallel
    // (network is the bottleneck, so that's the win). Completed indices live in a shared Set,
    // persisted (serialized) so resume skips them; `uploadedChunks` is kept in sync for the
    // existing incomplete-filter / resume-% readers.
    const done = new Set(
      Array.isArray(pendingRecord?.completedChunks)
        ? pendingRecord.completedChunks.filter((n) => Number.isInteger(n) && n >= 0 && n < totalChunks)
        : Array.from({ length: Math.min(pendingRecord?.uploadedChunks ?? 0, totalChunks) }, (_, i) => i)
    );
    const parallel = createParallelControl();
    const urlPool = createUploadUrlPool(authenticatedFetch, uploadId, totalChunks, (i) => done.has(i));
    let nextIndex = 0;
    let fatal = null;
    let cancelled = false;
    let lockedOut = false;

    const pickNext = () => {
      while (nextIndex < totalChunks && done.has(nextIndex)) nextIndex += 1;
      return nextIndex < totalChunks ? nextIndex++ : -1;
    };

    let persistChain = Promise.resolve();
    const persistProgress = () => {
      persistChain = persistChain.then(() =>
        db.pendingUploads.update(pendingId, {
          completedChunks: Array.from(done),
          uploadedChunks: done.size
        }).catch(() => {})
      );
      return persistChain;
    };

    // Fingerprint of the most recently encrypted chunk, read from the exact buffer that is
    // handed to uploadChunkWithRetry. Carried across emits because updateUploadProgress
    // REPLACES the progress entry instead of merging it, so an emit without the value would
    // silently drop it again.
    let lastChunkHash = null;
    let lastChunkHashIndex = null;

    const emitProgress = () => emit({
      percent: Math.round((done.size / totalChunks) * 100),
      phase: 'uploading',
      chunkIndex: done.size,
      bytesDone: Math.min(done.size * chunkSize, totalBytes),
      ...(lastChunkHash ? { chunkHash: lastChunkHash, chunkHashIndex: lastChunkHashIndex } : {})
    });

    let running = 0;
    const pool = [];
    const hasMore = () => {
      while (nextIndex < totalChunks && done.has(nextIndex)) nextIndex += 1;
      return nextIndex < totalChunks;
    };
    const spawnWorkers = () => {
      while (running < parallel.target && !fatal && !cancelled && !lockedOut && hasMore()) {
        running += 1;
        let left = false;
        const leave = () => { if (!left) { left = true; running -= 1; } };
        pool.push(uploadWorker(leave).finally(leave));
      }
    };

    async function uploadWorker(leave) {
      for (;;) {
        if (fatal || cancelled || lockedOut) return;
        // Ist die Zielzahl gesunken, beendet sich dieser Strang. `leave` zaehlt sofort herunter,
        // damit nicht mehrere Straenge gleichzeitig aussteigen.
        if (running > parallel.target) { leave(); return; }
        if (isCancelled && isCancelled(pendingId)) { cancelled = true; cancelController.abort(); return; }
        if (getActiveKey && !getActiveKey()) { lockedOut = true; return; }
        const c = pickNext();
        if (c === -1) return;

        const sliceStart = c * chunkSize;
        const sliceEnd = Math.min(sliceStart + chunkSize, file.size);
        let encryptedChunk;
        let plainBytes = 0;
        let encMs = 0;
        try {
          const chunkBuffer = await file.slice(sliceStart, sliceEnd).arrayBuffer();
          plainBytes = chunkBuffer.byteLength;
          const encStart = performance.now();
          encryptedChunk = await encryptChunk(chunkBuffer, chunkKey, fileUuid, c);
          encMs = performance.now() - encStart;
        } catch (e) {
          if (isCancelled && isCancelled(pendingId)) { cancelled = true; return; }
          fatal = fatal || { networkError: true };
          return;
        }

        // Taken here, from the same `encryptedChunk` reference that goes to the network on the
        // next line. No second encryption pass for display purposes.
        lastChunkHash = chunkFingerprint(encryptedChunk);
        lastChunkHashIndex = c;
        pushMachineEvent({
          kind: 'encrypt',
          target: 'cloud',
          fileName: file.name,
          chunkIndex: c,
          totalChunks,
          plainBytes,
          cipherBytes: encryptedChunk.length,
          fingerprint: lastChunkHash,
          ms: encMs
        });
        emitProgress();

        const result = await uploadChunkWithRetry(
          authenticatedFetch, uploadId, c, encryptedChunk,
          () => cancelled || (isCancelled ? isCancelled(pendingId) : false),
          cancelController.signal,
          { fileName: file.name, totalChunks },
          urlPool
        );

        if (result.ok) {
          done.add(c);
          parallel.onSuccess(encryptedChunk.length, result.retries > 0);
          spawnWorkers();
          await persistProgress();
          emitProgress();
        } else if (result.cancelled) {
          cancelled = true;
          return;
        } else {
          fatal = fatal || result; // storageUnavailable | sessionExpired | networkError
          return;
        }
      }
    }

    emitProgress();
    spawnWorkers();
    while (pool.length) await Promise.all(pool.splice(0));

    if (cancelled || (isCancelled && isCancelled(pendingId))) {
      return { ok: false, reason: 'cancelled' };
    }
    if (lockedOut) {
      await persistProgress();
      emitProgress();
      return {
        ok: false, reason: 'locked_strict', resumable: true, pendingId,
        message: `Upload von „${file.name}" pausiert: Tresor wurde streng gesperrt.`
      };
    }
    if (fatal) {
      if (fatal.storageUnavailable) {
        await abortPendingUpload(authenticatedFetch, pendingId, uploadId);
        return { ok: false, reason: 'storage_offline', message: `„${file.name}" konnte nicht hochgeladen werden (Speicher vorübergehend nicht erreichbar).` };
      }
      if (fatal.sessionExpired) {
        await abortPendingUpload(authenticatedFetch, pendingId, uploadId);
        return { ok: false, reason: 'session_expired', message: `Upload-Sitzung für „${file.name}" abgelaufen. Bitte erneut hochladen.` };
      }
      await persistProgress();
      emitProgress();
      return { ok: false, reason: 'network_error', resumable: true, pendingId, message: `Upload von „${file.name}" unterbrochen. Wird beim nächsten Entsperren fortgesetzt.` };
    }

    emit({ percent: Math.round(((totalChunks - 0.05) / totalChunks) * 100), phase: 'finalizing', chunkIndex: totalChunks, bytesDone: file.size });

    const completeRes = await authenticatedFetch(`${API_URL}/upload/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uploadId, totalChunks, chunkSize })
    });

    if (!completeRes.ok) {
      if (completeRes.status === 404) {
        await abortPendingUpload(authenticatedFetch, pendingId, uploadId);
        return {
          ok: false,
          reason: 'session_expired',
          message: `Upload-Sitzung für „${file.name}" abgelaufen. Bitte erneut hochladen.`
        };
      }
      return {
        ok: false,
        reason: 'complete_failed',
        message: `„${file.name}" konnte nicht abgeschlossen werden.`
      };
    }

    const recorded = await finalizeLocalFile({
      file,
      activeKey,
      fileUuid,
      chunkSize,
      folderId: targetFolderId,
      getFileType,
      formatBytes,
      getActiveKey,
      uploadId,
      totalChunks,
      skipLocalBlob: true,
      chunkKey,
      wrappedFileKey,
      onFinalizeProgress: (c, chunks, bytesDone) => {
        const base = 92;
        const span = 7;
        emit({
          percent: Math.min(99, base + Math.round(((c + 1) / chunks) * span)),
          phase: 'finalizing',
          chunkIndex: c + 1,
          bytesDone,
          totalChunks: chunks
        });
      }
    });

    if (onFileRecorded) {
      try {
        await onFileRecorded({
          type: 'file_add',
          clientId: recorded.clientId,
          parentClientId: recorded.folderClientId,
          name: recorded.name,
          encryptionId: recorded.encryptionId,
          wrappedFileKey: recorded.wrappedFileKey,
          uploadId: recorded.uploadId,
          totalChunks: recorded.totalChunks,
          chunkSize: recorded.chunkSize,
          sizeBytes: recorded.sizeBytes,
          sizeLabel: recorded.sizeLabel,
          mimeType: recorded.mimeType,
          fileType: recorded.fileType
        });
      } catch (journalErr) {
        console.error('Journal sync after upload failed', journalErr);
      }
    }

    await db.pendingUploads.delete(pendingId);
    await db.pendingFileBlobs.delete(pendingId);
    resumeFiles.delete(pendingId);
    emit({ percent: 100, phase: 'done', chunkIndex: totalChunks, bytesDone: file.size }, true);

    return { ok: true, fileName: file.name, pendingId };
  } catch (err) {
    console.error('Upload job failed', err);
    const detail = err?.message && !err.message.includes('UPLOAD_LOCKED')
      ? ` (${err.message})`
      : '';
    if (pendingId && uploadId) {
      // Keep pending row for resume; don't abort quota unless unrecoverable
    }
    return {
      ok: false,
      reason: 'crash',
      message: `Upload von „${file?.name || pendingRecord?.fileName || 'Datei'}" fehlgeschlagen${detail}.`,
      pendingId,
      uploadId
    };
  }
}

export async function loadIncompletePendingUploads() {
  const all = await openRecords('pending', await db.pendingUploads.toArray());
  return all.filter((p) => (p.uploadedChunks || 0) < (p.totalChunks || 0));
}
